import { useEffect, useRef, useState } from "react";
import api from "api/api";

//Props Validations
import PropTypes from "prop-types";

// MUI components
import { useTheme } from "@mui/material/styles";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// import mui icons
import ScienceIcon from "@mui/icons-material/Science";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

// import utils

import { consumerFilterPayload } from "../../../utils/consumerFilterUtils";
import { generateBoxAnalysisPayload, generateBoxAnalysisPayloadBySelection } from "../../../utils/boxAnalysisUtils";
import { getTTestFilterWarningFlag } from "../../../utils/analysisTable/tTestutils";
import { getDataView } from "../../../utils/analysisTable/analysisUtils";
import SortFinalData from "../../../utils/SortFinalData";

// import components
import Loader from "../Loader";
import SampleSizeWarning from "../SampleSizeWarning";

// redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadTestAnalysisType, loadTTestJarAnalysisType, loadTTestPerformed, loadTTestType, loadTTestConfidenceInterval, loadTTestProducts, loadTTestCoreDataId, loadTTestCheckedDataTypeForTest, loadTTestAnalysisType, loadTTestBoxAnalysis, loadTTestSelections, loadTTestFaLegends, loadTTestSelectedProducts } from "store/features/ttestSlice";
import { loadSignificanceToggleState, loadDisableSignificanceToggleState } from "store/features/analysisResultsSlice";

const itmRecallTTestType = ["Recall", "ITM", "Both"];
const confidenceIntervalTTestType = [0.8, 0.9, 0.95, 0.99];

const items = [
    { value: 0, label: "No T-Test", elementValue: "No T-Test", elementLabel: "No T-Test" },
    { value: 1, label: "Parametric", elementValue: "Student's T Test Comparisons", elementLabel: "Student's T Test Comparisons" },
    { value: 2, label: "Non-parametric", elementValue: "Kruskall Wallis with post-hoc Dunn Test", elementLabel: "Kruskall Wallis with post-hoc Dunn Test" },
];

const noTTestButton = { value: 0, label: "No T-Test", elementValue: "No T-Test", elementLabel: "No T-Test" };

function TTestDialog({ tabData = {}, analysisLevelData = {}, functionsSettings = {}, variableCategory = "", isITMDataShown = false, tTestBtnDisabled = false, isFragranceAssociationSection = false, overallToggleEnabled = false, percentageToggle = false, isJarAnalysis = false, module = "" }) {
    const dispatch = useDispatch();
    const { productsSecondaryDataState, fragranceAssociationDataState = [] } = tabData;
    const { moduleName, analysisTypeValue, boxAnalysisTypeSelection, boxAnalysisSelection, mappings = [], fragranceAssociationMappings = [] } = analysisLevelData;
    const { setIsFisher, setLegends, setFaLegends, setTTestResult, setTestLabel, setTestLabel2, clearResult } = functionsSettings;
    const [selectedSection, setSelectedSection] = useState("overall");
    const boxAnalysisTTestType = boxAnalysisTypeSelection.length == 2 ? [...boxAnalysisTypeSelection, "Both"] : boxAnalysisTypeSelection;
    const jarBoxAnalysisTTestType = ["Top 2 Box", "Middle Box", "Bottom 2 Box", "All"];

    let isPercentageValues;
    if (isFragranceAssociationSection) {
        isPercentageValues = percentageToggle;
    } else {
        isPercentageValues = true;
        if (moduleName !== "justRight" && (analysisTypeValue.value === "box_analysis" || analysisTypeValue.value === "percentage")) {
            isPercentageValues = true;
        } else if (moduleName === "justRight") {
            isPercentageValues = true;
        } else if (moduleName === "fragranceAssociation") {
            isPercentageValues = true;
        } else if (analysisTypeValue.value === "mean" || analysisTypeValue.value === "count") {
            isPercentageValues = false;
        } else {
            isPercentageValues = false;
        }
    }

    const itemEls = useRef({});
    const theme = useTheme();

    const [parametricTestButton, setParametricTestButton] = useState({ value: 1, label: "Parametric", elementValue: "Parametric Student's T Test Comparisons", elementLabel: "Student's T Test Comparisons" });
    const [nonParametricTestButton, setNonParametricTestButton] = useState({ value: 2, label: "Non-parametric", elementValue: "Non-parametric Kruskall Wallis with post-hoc Dunn Test", elementLabel: "Kruskall Wallis with post-hoc Dunn Test" });

    // redux store data
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const consumerFilterStoreData = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const absoluteToggle = useSelector((state) => state.analysisResultsStore[`${moduleName}AbsoluteToggle`]);

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = isHairCare || isSkinCare;
    const filteredConfidenceIntervals = isOralCare || isSkinCleansing ? confidenceIntervalTTestType : moduleName === "fragranceMetrics" || moduleName === "preference" ? confidenceIntervalTTestType.filter((ele) => ele !== 0.9 && ele !== 0.8) : confidenceIntervalTTestType.filter((ele) => ele !== 0.8);

    // t-test details
    const [tTestDialogueOpen, setTTestDialogueOpen] = useState(false);
    const [disableResultButton, setDisableResultButton] = useState(true);
    const [selectedProductCoreDataId, setSelectedProductCoreDataId] = useState(0);
    const [checkedProductsForTest, setCheckedProductsForTest] = useState([]); // products
    const [checkedConfidenceIntervalForTest, setCheckedConfidenceIntervalForTest] = useState(""); // cf
    const [checkedTestTypeForTest, setCheckedTestTypeForTest] = useState(""); // test name
    const [checkedDataTypeForTest, setCheckedDataTypeForTest] = useState(""); // itm recall both
    const [checkedBoxAnalysisType, setCheckedBoxAnalysisType] = useState(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : ""); // top bottom both
    const [checkedJarBoxAnalysisType, setCheckedJarBoxAnalysisType] = useState("");
    const [fisherLSDComparisonType, setFisherLSDComparisonType] = useState("");
    const [zTestComparisonType, setZTestComparisonType] = useState("");

    const [isTTestButtonDisabled, setIsTTestButtonDisabled] = useState(false);
    const [isTTestLoading, setIsTTestLoading] = useState(false);
    const [isParametricButtonDisabled, setIsParametricButtonDisabled] = useState(false);
    const [isNonParametricButtonDisabled, setIsNonParametricButtonDisabled] = useState(false);
    const [is1TailedDisabled, setIs1TailedDisabled] = useState(false);
    // const [checkedComparisonProductsForTest, setCheckedComparisonProductsForTest] = useState([]);

    // flag for warning - if applied filters are not same
    const [tTestWarningFlag, setTTestWarningFlag] = useState(true);
    const [showDialogSampleWarning, setShowDialogSampleWarning] = useState(false);
    let totalProducts = 0;
    const [tTestSelections, setTTestSelections] = useState({
        "overall": {},
        "fragranceAssociation": {}
    });

    const [loader, setLoader] = useState(false);
    const [checkAvailibility, setCheckAvailability] = useState([]);
    const moduleNameForResponse = {
        "claims": "claims",
        "correlation": "correlation",
        "jar": "jar",
        "justRight": "just_right",
        "overall": "overall",
        "secondary": "secondary",
        "agreement": "agreement",
        "fragranceAssociation": "fragrance_association",
        "fragranceMetrics": "fragrance_metrics",
        "overallexpressClt": "fragrance_metrics",
        "overallclt": "fragrance_metrics",
        "secondaryexpressClt": "fragrance_metrics",
        "secondaryclt": "fragrance_metrics",
        "preference": "preference",
        "productPreference": "product_preference"
    };

    const [showTwoProduct, setShowTwoProduct] = useState(true);
    const [showMultipleProduct, setShowMultipleProduct] = useState(true);

    const showJarAnalysisNote = analysisTypeValue.value !== "box_analysis" && moduleName === "secondary" && variableCategory === "Fragrance Metrics Analysis,JAR";

    const handleSectionChange = (event, newSection) => {
        if (newSection !== null) {
            setSelectedSection(newSection);
            const savedSelections = tTestSelections[newSection];

            if (savedSelections && Object.keys(savedSelections).length > 0) {
                setCheckedProductsForTest(savedSelections.products || []);
                setCheckedTestTypeForTest(savedSelections.test_id || "");
                setCheckedConfidenceIntervalForTest(savedSelections.confidence_interval || "");

                if (savedSelections.test_id === 4) {
                    setFisherLSDComparisonType(savedSelections.within_product_text || "");
                    setZTestComparisonType("");
                } else if (savedSelections.test_id === 6) {
                    setZTestComparisonType(savedSelections.within_product_text || "");
                    setFisherLSDComparisonType("");
                } else {
                    setFisherLSDComparisonType("");
                    setZTestComparisonType("");
                }

            } else {
                setCheckedTestTypeForTest("");
                setCheckedConfidenceIntervalForTest("");
                setCheckedBoxAnalysisType(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : "");
                setFisherLSDComparisonType("");
                setZTestComparisonType("");
                setCheckedProductsForTest([]);
            }
        }
    };

    function checkSampleSizeWarning(productsToTest) {
        const hasLowSampleSize = productsToTest.some((p) => Number(p?.sampleSize) > 0 && Number(p?.sampleSize) < 75);
        setShowDialogSampleWarning(hasLowSampleSize);
    }

    useEffect(() => {
        if (selectedStudy.length === 1 && productsSecondaryDataState.length === 2) {
            const isDisable = productsSecondaryDataState.some((datum) => datum.sampleSize === 0);
            const disableButtonByHiddenProduct = productsSecondaryDataState.some((product) => productColumnVisibility[`checked--${product.title.split(" ").join("_")}__${product.coreDataId}`]);
            setIsTTestButtonDisabled(isDisable || disableButtonByHiddenProduct);
        } else if (productsSecondaryDataState.length > 2) {
            let noOfHiddenProducts = 0;
            let tempProducts = [];
            productsSecondaryDataState.forEach((product) => {
                if (productColumnVisibility[`checked--${product.title.split(" ").join("_")}__${product.coreDataId}`]) {
                    noOfHiddenProducts += 1;
                } else {
                    tempProducts.push(product);
                }
            });
            if (productsSecondaryDataState.length - noOfHiddenProducts < 2) {
                setIsTTestButtonDisabled(true);
            } else if (productsSecondaryDataState.length - noOfHiddenProducts === 2) {
                const isDisable = tempProducts.some((datum) => datum.sampleSize === 0);
                setIsTTestButtonDisabled(isDisable);
            }
        }
    }, [productsSecondaryDataState, productColumnVisibility, selectedStudy]);

    useEffect(() => {
        if (isFragranceAssociationSection) {
            // if (checkedTestTypeForTest === 0) {
            //     setDisableResultButton(false);
            // } else if (checkedTestTypeForTest === 7) {
            //     // Binomial Test
            //     setDisableResultButton(checkedProductsForTest.length !== 2 || checkedConfidenceIntervalForTest === "");
            // } else if (checkedTestTypeForTest === 4) {
            //     // Fisher's LSD
            //     if (fisherLSDComparisonType === "Within") {
            //         setDisableResultButton(checkedProductsForTest.length < 1 || checkedConfidenceIntervalForTest === "");
            //     } else if (fisherLSDComparisonType === "Across") {
            //         setDisableResultButton(checkedProductsForTest.length < 3 || checkedConfidenceIntervalForTest === "");
            //     } else {
            //         setDisableResultButton(true);
            //     }
            // } else if (checkedTestTypeForTest === 6) {
            //     // Z Test
            //     if (zTestComparisonType === "Within") {
            //         setDisableResultButton(checkedProductsForTest.length < 1 || checkedConfidenceIntervalForTest === "");
            //     } else if (zTestComparisonType === "Across") {
            //         setDisableResultButton(checkedProductsForTest.length < 2 || checkedConfidenceIntervalForTest === "");
            //     } else {
            //         setDisableResultButton(true);
            //     }
            // } else {
            //     setDisableResultButton(true);
            // }
            let checkFlag = [true, true];
            if (Object.keys(tTestSelections["fragranceAssociation"]).length) {
                Object.keys(tTestSelections).forEach((item, i) => {
                    if (tTestSelections[item]?.test_id && parseInt(tTestSelections[item]?.test_id) === 0) {
                        checkFlag[i] = false;
                    } else if (tTestSelections[item]?.test_id && tTestSelections[item]?.confidence_interval && parseInt(tTestSelections[item]?.test_id) !== 0) {
                        const productsValidationForTest = validateProductsForTest(tTestSelections[item]?.products, tTestSelections[item]?.test_id, item);
                        checkFlag[i] = !productsValidationForTest;
                    } else {
                        checkFlag[i] = true;
                    }
                });
                const checkFlagStatus = overallToggleEnabled ? checkFlag.every(flag => flag === false) : checkFlag[1] === false;
                setDisableResultButton(!checkFlagStatus);
            } else {
                setDisableResultButton(true);
            }
        } else {
            if (checkedTestTypeForTest === 0) {
                setDisableResultButton(false);
            } else if (checkedProductsForTest.length >= 2 && checkedConfidenceIntervalForTest !== "" && checkedTestTypeForTest !== "") {
                const studyTypeMethod = selectedStudy[0].study_type_method__title;
                if (isITMDataShown) {
                    if (checkedTestTypeForTest === 6 && analysisTypeValue.value === "box_analysis" && checkedBoxAnalysisType === "") {
                        setDisableResultButton(true);
                    } else if (checkedDataTypeForTest !== "") {
                        setDisableResultButton(false);
                    } else {
                        setDisableResultButton(true);
                    }
                } else if (studyTypeMethod === "CLT" && moduleName === "secondary" && checkedTestTypeForTest === 6 && analysisTypeValue.value === "box_analysis" && isJarAnalysis && checkedBoxAnalysisType !== "" && checkedJarBoxAnalysisType === "") {
                    setDisableResultButton(true);
                } else if (checkedTestTypeForTest === 6 && analysisTypeValue.value === "box_analysis" && checkedBoxAnalysisType === "") {
                    setDisableResultButton(true);
                } else {
                    setDisableResultButton(false);
                }
            } else if (checkedProductsForTest.length === 1 && checkedConfidenceIntervalForTest !== "" && checkedTestTypeForTest !== "" && moduleName === "productPreference") {
                setDisableResultButton(false);
            } else {
                setDisableResultButton(true);
            }
        }
    }, [checkedProductsForTest, checkedConfidenceIntervalForTest, checkedTestTypeForTest, checkedDataTypeForTest, isITMDataShown, checkedBoxAnalysisType, isFragranceAssociationSection, selectedSection, fisherLSDComparisonType, zTestComparisonType, moduleName, analysisTypeValue, tTestSelections, checkedJarBoxAnalysisType]);

    useEffect(() => {
        if (checkedTestTypeForTest === 1) {
            setIs1TailedDisabled(false);
            setCheckedConfidenceIntervalForTest("");
        } else {
            setIs1TailedDisabled(true);
            setCheckedConfidenceIntervalForTest("");
        }
    }, [checkedTestTypeForTest]);

    const validateProductsForTest = (products, testId, section) => {
        if (parseInt(testId) === 1) {
            return products?.length === 2;
        } else if (parseInt(testId) === 2) {
            return products?.length === 2;
        } else if (parseInt(testId) === 7) {
            // Binomial Test
            return products?.length === 2;
        } else if (parseInt(testId) === 4) {
            // Fisher's LSD
            if (section === "fragranceAssociation") {
                if (tTestSelections[section]?.within_product_text) {
                    if (tTestSelections[section]?.within_product_text === "Within") {
                        return products?.length >= 1;
                    } else if (fisherLSDComparisonType === "Across") {
                        return products?.length > 2;
                    }
                } else {
                    return false;
                }
            } else {
                return products?.length > 2;
            }
        } else if (parseInt(testId) === 6) {
            // Z Test
            if (section === "fragranceAssociation" && tTestSelections[section]?.within_product_text) {
                if (tTestSelections[section]?.within_product_text === "Within") {
                    return products?.length >= 1;
                } else if (tTestSelections[section]?.within_product_text === "Across") {
                    return products?.length >= 2;
                }
            } else {
                return false;
            }
        } else {
            return true;
        }
    };

    const getParametricTestTwoProduct = (isPercentage) => {
        if (isPercentage) {
            setIsParametricButtonDisabled(false);
            if (moduleName === "productPreference") {
                return { value: 6, label: "Parametric", elementValue: "product_preference z test", elementLabel: "Z Test" };
            } else {
                return { value: 6, label: "Parametric", elementValue: "Z Test", elementLabel: "Z Test" };
            }
        } else {
            if (moduleName === "fragranceMetrics") {
                setIsParametricButtonDisabled(false);
                return { value: 4, label: "Parametric", elementValue: "Fisher's LSD pairwise comparisons", elementLabel: "Fisher's LSD pairwise comparisons" };
            } else if (moduleName === "preference" || moduleName === "productPreference") {
                setIsParametricButtonDisabled(true);
                return { value: 4, label: "Parametric", elementValue: "Fisher's LSD pairwise comparisons", elementLabel: "Fisher's LSD pairwise comparisons (after data transformation)" };
            } else {
                setIsParametricButtonDisabled(false);
                return { value: 1, label: "Parametric", elementValue: "Student's T Test Comparisons", elementLabel: "Student's T Test Comparisons" };
            }
        }
    };

    const getParametricTestThreeProduct = (isPercentage) => {
        if (isPercentage) {
            if (moduleName === "preference") {
                setIsParametricButtonDisabled(false);
                return { value: 6, label: "Parametric", elementValue: "Z Test", elementLabel: "Z Test" };
            } else if (moduleName === "productPreference") {
                setIsParametricButtonDisabled(false);
                return { value: 6, label: "Parametric", elementValue: "product_preference z test", elementLabel: "Z Test" };
            } else {
                setIsParametricButtonDisabled(false);
                return { value: 6, label: "Parametric", elementValue: "Z Test", elementLabel: "Z Test" };
            }
        } else {
            setIsParametricButtonDisabled(false);
            if (moduleName === "preference") {
                return { value: 4, label: "Parametric", elementValue: "Fisher's LSD pairwise comparisons", elementLabel: "Fisher's LSD pairwise comparisons (after data transformation)" };
            } else if (moduleName === "productPreference") {
                return { value: 4, label: "Parametric", elementValue: "product_preference fisher's lsd", elementLabel: "Fisher's LSD pairwise comparisons (after data transformation)" };
            } else {
                return { value: 4, label: "Parametric", elementValue: "Fisher's LSD pairwise comparisons", elementLabel: "Fisher's LSD pairwise comparisons" };
            }
        }
    };

    const getNonParametricTestTwoProduct = (isPercentage) => {
        if (isPercentage) {
            if (moduleName === "preference" || moduleName === "productPreference") {
                setIsNonParametricButtonDisabled(true);
                return { value: 7, label: "Non-parametric", elementValue: "Binomial (P Value Two Sided​)", elementLabel: "Binomial (P Value Two Sided​)" };
            } else {
                setIsNonParametricButtonDisabled(true);
                return { value: 2, label: "Non-parametric", elementValue: "Non-parametric Kruskall Wallis with post-hoc Dunn Test", elementLabel: "Kruskall Wallis with post-hoc Dunn Test" };
            }
        } else {
            if (moduleName === "preference") {
                setIsNonParametricButtonDisabled(false);
                return { value: 7, label: "Non-parametric", elementValue: "Binomial (P Value Two Sided​)", elementLabel: "Binomial (P Value Two Sided​)" };
            } else if (moduleName === "productPreference") {
                setIsNonParametricButtonDisabled(false);
                return { value: 7, label: "Non-parametric", elementValue: "product_preference binomial test", elementLabel: "Binomial (P Value Two Sided​)" };
            } else {
                setIsNonParametricButtonDisabled(false);
                return { value: 2, label: "Non-parametric", elementValue: "Non-parametric Kruskall Wallis with post-hoc Dunn Test", elementLabel: "Kruskall Wallis with post-hoc Dunn Test" };
            }
        }
    };

    const getNonParametricTestThreeProduct = () => {
        if (moduleName === "preference") {
            if (checkedTestTypeForTest === 7) {
                setCheckedTestTypeForTest("");
            }
            setIsNonParametricButtonDisabled(true);
            return { value: 7, label: "Non-parametric", elementValue: "Binomial (P Value Two Sided​)", elementLabel: "Binomial (P Value Two Sided​)" };
        } else if (moduleName === "productPreference") {
            setIsNonParametricButtonDisabled(true);
            return { value: 7, label: "Non-parametric", elementValue: "product_preference binomial test", elementLabel: "Binomial (P Value Two Sided​)" };
        } else {
            setIsNonParametricButtonDisabled(false);
            return { value: 5, label: "Non-parametric", elementValue: "Post-hoc Dunn Test", elementLabel: "Post-hoc Dunn Test" };
        }
    };

    useEffect(() => {
        if (checkedProductsForTest.length !== 0) {
            let id = checkedProductsForTest[0].coreDataId;
            setSelectedProductCoreDataId(id);
        } else {
            setSelectedProductCoreDataId(0);
        }

        if (isFragranceAssociationSection && selectedSection === "fragranceAssociation") {
            if (isPercentageValues) {
                setParametricTestButton({
                    value: 6,
                    label: "Parametric Test",
                    elementValue: "Z Test",
                    elementLabel: "Z Test",
                });
                setNonParametricTestButton({
                    value: 7,
                    label: "Non-parametric Test",
                    elementValue: "Binomial (P Value two sided​)",
                    elementLabel: "Binomial (P Value two sided​) (Across 2 Products Only)",
                });
                setIsParametricButtonDisabled(false);
                setIsNonParametricButtonDisabled(true);
            } else {
                setParametricTestButton({
                    value: 4,
                    label: "Parametric Test",
                    elementValue: "Fisher's LSD pairwise comparisons (after data transformation)",
                    elementLabel: "Fisher's LSD pairwise comparisons (after data transformation)",
                });
                setNonParametricTestButton({
                    value: 7,
                    label: "Non-parametric Test",
                    elementValue: "Binomial (P Value two sided​)",
                    elementLabel: "Binomial (P Value two sided​) (Across 2 Products Only)",
                });
                setIsParametricButtonDisabled(false);
                setIsNonParametricButtonDisabled(false);
            }
        } else if (isFragranceAssociationSection && selectedSection === "overall") {
            if (checkedProductsForTest.length > 2) {
                if ([1, 2].includes(checkedTestTypeForTest)) {
                    setCheckedTestTypeForTest("");
                    itemEls.current[1]?.classList.remove("test-type-selected");
                    itemEls.current[2]?.classList.remove("test-type-selected");
                }
                setParametricTestButton({ value: 4, label: "Parametric", elementValue: "Fisher's LSD pairwise comparisons", elementLabel: "Fisher's LSD pairwise comparisons" });
                setNonParametricTestButton({ value: 5, label: "Non-parametric", elementValue: "Post-hoc Dunn Test", elementLabel: "Post-hoc Dunn Test" });
            } else {
                if ([4, 5].includes(checkedTestTypeForTest)) {
                    setCheckedTestTypeForTest("");
                    itemEls.current[1]?.classList.remove("test-type-selected");
                    itemEls.current[2]?.classList.remove("test-type-selected");
                }
                setParametricTestButton({ value: 1, label: "Parametric", elementValue: "Student's T Test Comparisons", elementLabel: "Student's T Test Comparisons" });
                setNonParametricTestButton({ value: 2, label: "Non-parametric", elementValue: "Kruskall Wallis with post-hoc Dunn Test", elementLabel: "Kruskall Wallis with post-hoc Dunn Test" });
            }
            setIsParametricButtonDisabled(false);
            setIsNonParametricButtonDisabled(false);
        } else if (moduleName === "productPreference") {
            // const productsArr = productsSecondaryDataState?.[0]?.title?.split("vs");
            const productCntArr = productsSecondaryDataState.map(ele => ele?.products?.length || 0);
            const uniqueArr = [...new Set(productCntArr)];
            if (uniqueArr.length === 1 && uniqueArr[0] === 2) {
                const parametricTest = getParametricTestTwoProduct(isPercentageValues);
                setParametricTestButton(parametricTest);
                const nonParametricTest = getNonParametricTestTwoProduct(isPercentageValues);
                setNonParametricTestButton(nonParametricTest);
            } else if ((uniqueArr.length === 1 && uniqueArr[0] > 2) || (uniqueArr.length > 1 && !uniqueArr.includes(2))) {
                const parametricTest = getParametricTestThreeProduct(isPercentageValues);
                setParametricTestButton(parametricTest);
                const nonParametricTest = getNonParametricTestThreeProduct();
                setNonParametricTestButton(nonParametricTest);
            } else if (uniqueArr.length > 1 && uniqueArr.includes(2)) {
                const parametricTest = isPercentageValues ? { value: 6, label: "Parametric", elementValue: "product_preference z test", elementLabel: "Z Test" } : { value: 4, label: "Parametric", elementValue: "product_preference fisher's lsd", elementLabel: "Fisher's LSD pairwise comparisons (after data transformation)" };
                setParametricTestButton(parametricTest);
                setIsParametricButtonDisabled(false);
                const nonParametricTest = { value: 7, label: "Non-parametric", elementValue: "product_preference binomial test", elementLabel: "Binomial (P Value Two Sided​)" };
                setNonParametricTestButton(nonParametricTest);
                setIsNonParametricButtonDisabled(isPercentageValues ? true : false);
                if (checkedProductsForTest.length === 1 && !isPercentageValues) {
                    if (checkedProductsForTest[0].products.length === 2) {
                        setIsParametricButtonDisabled(true);
                    } else {
                        setIsNonParametricButtonDisabled(true);
                    }
                } else if (checkedProductsForTest.length > 1 && !isPercentageValues) {
                    if (checkedProductsForTest.every((p) => p.products.length === 2)) {
                        setIsParametricButtonDisabled(true);
                    } else if (checkedProductsForTest.every((p) => p.products.length > 2)) {
                        setIsNonParametricButtonDisabled(true);
                    }
                }
            }
            // if (productsArr?.length === 2) {
            //     const parametricTest = getParametricTestTwoProduct(isPercentageValues);
            //     setParametricTestButton(parametricTest);
            //     const nonParametricTest = getNonParametricTestTwoProduct(isPercentageValues);
            //     setNonParametricTestButton(nonParametricTest);
            // } else if (productsArr?.length > 2) {
            //     const parametricTest = getParametricTestThreeProduct(isPercentageValues);
            //     setParametricTestButton(parametricTest);
            //     const nonParametricTest = getNonParametricTestThreeProduct();
            //     setNonParametricTestButton(nonParametricTest);
            // }
        } else {
            if (checkedProductsForTest.length >= 3) {
                // ? fisher LSD
                if (checkedTestTypeForTest === 1) {
                    setCheckedTestTypeForTest("");
                    setCheckedBoxAnalysisType(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : "");
                    itemEls.current[1].classList.remove("test-type-selected");
                }
                const studyTypeMethod = selectedStudy[0].study_type_method__title;
                if (!(studyTypeMethod === "CLT" && checkedTestTypeForTest === 6 && (moduleName === "overall" || moduleName === "secondary"))) {
                    const parametricTest = getParametricTestThreeProduct(isPercentageValues);
                    setParametricTestButton(parametricTest);
                }

                if (checkedTestTypeForTest === 2) {
                    setCheckedTestTypeForTest("");
                    setCheckedBoxAnalysisType(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : "");
                    itemEls.current[2].classList.remove("test-type-selected");
                }
                if (!(studyTypeMethod === "CLT" && checkedTestTypeForTest === 6 && (moduleName === "overall" || moduleName === "secondary"))) {
                    const nonParametricTest = getNonParametricTestThreeProduct();
                    setNonParametricTestButton(nonParametricTest);
                }
            } else {
                if (checkedTestTypeForTest === 4 && moduleName !== "fragranceMetrics") {
                    setCheckedTestTypeForTest("");
                    setCheckedBoxAnalysisType(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : "");
                    itemEls.current[1].classList.remove("test-type-selected");
                }

                const parametricTest = getParametricTestTwoProduct(isPercentageValues);
                setParametricTestButton(parametricTest);

                if (checkedTestTypeForTest === 5) {
                    setCheckedTestTypeForTest("");
                    setCheckedBoxAnalysisType(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : "");
                    itemEls.current[2].classList.remove("test-type-selected");
                }

                const nonParametricTest = getNonParametricTestTwoProduct(isPercentageValues);
                setNonParametricTestButton(nonParametricTest);
            }
        }
    }, [checkedProductsForTest, isPercentageValues, boxAnalysisTypeSelection, moduleName, selectedSection, isFragranceAssociationSection]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const handleTTestDialogueOpen = () => {
        resetTestDialogState();
        setTTestDialogueOpen(!tTestDialogueOpen);
        if (overallToggleEnabled) {
            setSelectedSection("overall");
        } else {
            setSelectedSection("fragranceAssociation");
        }
    };

    const handleTTestDialogueClose = () => {
        setTTestDialogueOpen(!tTestDialogueOpen);
        setTTestWarningFlag(true);
        if (moduleName === "fragranceAssociation") {
            setTTestSelections({
                "overall": {},
                "fragranceAssociation": {}
            });
        }
    };

    const handleTestTypeBackgroundColor = (event) => {
        let topId = event.target.id.split("-");
        items.forEach((item, index) => {
            itemEls.current[index]?.classList.remove("test-type-selected");
        });
        itemEls.current[topId[1]].classList.add("test-type-selected");
    };

    const handleInputChange = (e, name, product) => {
        if (name === "products") {
            if (e.target.checked) {
                if (moduleName === "productPreference") {
                    setCheckedProductsForTest((checkedItems) => {
                        const selectedProductsForTtest = [
                            ...checkedItems,
                            {
                                title: product.title,
                                coreDataId: product.coreDataId,
                                studyType: product.studyType,
                                studyTypeMethod: product.studyTypeMethod,
                                isCompetitorProduct: product.isCompetitorProduct,
                                scores: product.scores,
                                sampleSize: product.sampleSize,
                                products: product.products,
                                backendVariable: product.backendVariable,
                            },
                        ];
                        checkSampleSizeWarning(selectedProductsForTtest);

                        return selectedProductsForTtest;
                    });

                } else {
                    setCheckedProductsForTest((checkedItems) => {
                        const selectedProductsForTtest = [
                            ...checkedItems,
                            {
                                title: product.title,
                                coreDataId: product.coreDataId,
                                studyType: product.studyType,
                                studyTypeMethod: product.studyTypeMethod,
                                isCompetitorProduct: product.isCompetitorProduct,
                                scores: product.scores,
                                sampleSize: product.sampleSize,
                            },
                        ];
                        checkSampleSizeWarning(selectedProductsForTtest);

                        return selectedProductsForTtest;
                    });
                }

                const selectedProducts = [
                    ...checkedProductsForTest,
                    {
                        title: product.title,
                        coreDataId: product.coreDataId,
                        isCompetitorProduct: product.isCompetitorProduct,
                        scores: product.scores,
                    },
                ];
                const tTestWarning = getTTestFilterWarningFlag(selectedProducts, consumerFilterStoreData);
                setTTestWarningFlag(tTestWarning);
                if (moduleName === "fragranceAssociation") {
                    let tempObj = { ...tTestSelections };
                    if (!Object.keys(tempObj[selectedSection]).includes("products")) {
                        tempObj[selectedSection]["products"] = [];
                    }
                    tempObj[selectedSection]["products"].push(product);
                    checkSampleSizeWarning([...tempObj[selectedSection]["products"]]);
                    setTTestSelections({ ...tempObj })
                }
            } else {
                const filteredId = checkedProductsForTest.filter((ele) => ele.title !== e.target.value);
                setCheckedProductsForTest(filteredId);
                const tTestWarning = getTTestFilterWarningFlag(filteredId, consumerFilterStoreData);
                setTTestWarningFlag(tTestWarning);
                checkSampleSizeWarning(filteredId);
                if (moduleName === "fragranceAssociation") {
                    let tempObj = { ...tTestSelections };
                    const tempProducts = tempObj[selectedSection]["products"].filter(ele => ele.title !== product.title)
                    tempObj[selectedSection]["products"] = tempProducts;
                    checkSampleSizeWarning(tempProducts);
                    setTTestSelections({ ...tempObj });
                }
            }
        } else if (name === "confidenceInterval") {
            setCheckedConfidenceIntervalForTest(e.target.value);
            if (moduleName === "fragranceAssociation") {
                let tempObj = { ...tTestSelections };
                tempObj[selectedSection]["confidence_interval"] = e.target.value;
                setTTestSelections({ ...tempObj })
            }
        } else if (name === "testType") {
            const newTestType = Number(e.target.value);
            setCheckedTestTypeForTest(newTestType);
            setFisherLSDComparisonType("");
            setZTestComparisonType("");
            setCheckedConfidenceIntervalForTest("");

            if (moduleName === "productPreference") {
                if (Number(e.target.value) === 4) {
                    setShowTwoProduct(false);
                    setShowMultipleProduct(true);
                } else if (Number(e.target.value) === 7) {
                    setShowTwoProduct(true);
                    setShowMultipleProduct(false);
                } else {
                    setShowTwoProduct(true);
                    setShowMultipleProduct(true);
                }
            }

            let tempSelections = { ...tTestSelections };

            if (isFragranceAssociationSection && selectedSection === "fragranceAssociation") {
                setCheckedProductsForTest([]);
                if (!tempSelections[selectedSection]) {
                    tempSelections[selectedSection] = {};
                }
                tempSelections[selectedSection]["products"] = [];
            }

            if (moduleName === "fragranceAssociation") {
                const testName = getTestNameById(newTestType);
                if (!tempSelections[selectedSection]) {
                    tempSelections[selectedSection] = {};
                }
                tempSelections[selectedSection]["test_name"] = testName;
                tempSelections[selectedSection]["test_id"] = newTestType;
                tempSelections[selectedSection]["confidence_interval"] = "";

                if (newTestType !== 4 && newTestType !== 6 && selectedSection === "fragranceAssociation") {
                    delete tempSelections[selectedSection].within_product;
                    delete tempSelections[selectedSection].within_product_text;
                }
            }
            setTTestSelections(tempSelections);
        } else if (name === "dataType") {
            setCheckedDataTypeForTest(e.target.value);
        } else if (name === "boxAnalysisType") {
            setCheckedBoxAnalysisType(e.target.value);
        } else if (name === "jarBoxAnalysisType") {
            setCheckedJarBoxAnalysisType(e.target.value);
        } else if (name === "fisherLSDType") {
            setFisherLSDComparisonType(e.target.value);
            if (moduleName === "fragranceAssociation") {
                let tempObj = { ...tTestSelections };
                tempObj[selectedSection]["within_product"] = e.target.value === "Within";
                tempObj[selectedSection]["within_product_text"] = e.target.value;
                setTTestSelections({ ...tempObj })
            } else {
                setCheckedProductsForTest([]);
            }
        } else if (name === "zTestType") {
            setZTestComparisonType(e.target.value);
            if (moduleName === "fragranceAssociation") {
                let tempObj = { ...tTestSelections };
                tempObj[selectedSection]["within_product"] = e.target.value === "Within";
                tempObj[selectedSection]["within_product_text"] = e.target.value;
                setTTestSelections({ ...tempObj })
            } else {
                setCheckedProductsForTest([]);
            }
        }
    };

    const returnRadioButton = (value, label, type, selections, disabled = false) => {
        let checkedStatus = false;
        if (moduleName === "fragranceAssociation") {
            if (type === "confidenceInterval") {
                checkedStatus = parseFloat(tTestSelections?.[selectedSection]?.["confidence_interval"]) === value;
            } else if (type === "fisherLSDType") {
                checkedStatus = tTestSelections?.[selectedSection]?.["within_product_text"] === value;
            } else if (type === "zTestType") {
                checkedStatus = tTestSelections?.[selectedSection]?.["within_product_text"] === value;
            }
        } else {
            if (type === "confidenceInterval") {
                checkedStatus = parseFloat(checkedConfidenceIntervalForTest) === value;
            } else if (type === "boxAnalysisType") {
                checkedStatus = checkedBoxAnalysisType === value;
            } else if (type === "jarBoxAnalysisType") {
                checkedStatus = checkedJarBoxAnalysisType === value;
            } else {
                checkedStatus = checkedDataTypeForTest === value;
            }
        }

        return (
            <FormControlLabel
                key={value}
                sx={{
                    marginRight: "12px",
                    marginLeft: "12px",
                    "& .MuiTypography-root": {
                        fontSize: 12,
                        color: "#969696",
                    },
                    minWidth: "100px",
                    maxWidth: "150px",
                    borderRadius: "5px",
                    color: selections.toString() === value.toString() ? theme.palette.primary.main : theme.palette.text.primary,
                    background: selections.toString() === value.toString() ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                }}
                value={value}
                control={
                    <Radio
                        onChange={(e) => handleInputChange(e, type)}
                        value={value}
                        checked={checkedStatus}
                        sx={{
                            "& .MuiSvgIcon-root": {
                                fontSize: 16,
                                color: "#969696",
                            },
                            "& .MuiSvgIcon-root:nth-of-type(2)": {
                                fontSize: 16,
                                color: "#1976d2",
                            },
                        }}
                        disabled={disabled}
                    />
                }
                label={label}
                disabled={disabled}
            />
        );
    };

    const renderTestType = () => {
        const isNonparametrictestTypeShown = moduleName !== "fragranceMetrics";
        return (
            <FormControl sx={{ width: "100%" }}>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    onChange={handleTestTypeBackgroundColor}
                    sx={{
                        display: "flex",
                        justifyContent: isNonparametrictestTypeShown ? "space-between" : "flex-start",
                        gap: isNonparametrictestTypeShown ? "0px" : "40px",
                    }}
                    value={checkedTestTypeForTest}
                >
                    <div
                        key={0}
                        style={{ width: "30%" }}
                    >
                        <div
                            style={{ width: "100%" }}
                            className="test-type"
                            ref={(element) => (itemEls.current[0] = element)}
                        >
                            {noTTestButton.label}
                        </div>
                        <FormControlLabel
                            sx={{
                                "& .MuiTypography-root": {
                                    fontSize: 12,
                                    color: "#969696",
                                },
                            }}
                            value={noTTestButton.elementValue}
                            control={
                                <Radio
                                    onChange={(e) => handleInputChange(e, "testType")}
                                    value={0}
                                    checked={(moduleName === "fragranceAssociation" && tTestSelections?.[selectedSection]?.["test_id"] === 0) || parseInt(checkedTestTypeForTest) === 0}
                                    sx={{
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 16,
                                            color: "#969696",
                                        },
                                        "& .MuiSvgIcon-root:nth-of-type(2)": {
                                            fontSize: 16,
                                            color: "#1976d2",
                                        },
                                    }}
                                    id={"testType-" + 0}
                                />
                            }
                            size="small"
                            label={noTTestButton.elementLabel}
                            className="radio-label"
                        />
                    </div>

                    {parametricTestButton && (
                        <div
                            key={1}
                            style={{ width: "30%" }}
                        >
                            <div
                                style={{ width: "100%" }}
                                className={isParametricButtonDisabled ? "test-type test-type-disabled" : "test-type"}
                                ref={(element) => (itemEls.current[1] = element)}
                                disabled={isParametricButtonDisabled}
                            >
                                {parametricTestButton.label}
                            </div>
                            <FormControlLabel
                                sx={{
                                    "& .MuiTypography-root": {
                                        fontSize: 12,
                                        color: "#969696",
                                    },
                                }}
                                value={parametricTestButton.elementValue}
                                control={
                                    <Radio
                                        onChange={(e) => handleInputChange(e, "testType")}
                                        value={parametricTestButton.value}
                                        checked={(moduleName === "fragranceAssociation" && tTestSelections?.[selectedSection]?.["test_id"] == parametricTestButton.value) || checkedTestTypeForTest == parametricTestButton.value}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 16,
                                                color: "#969696",
                                            },
                                            "& .MuiSvgIcon-root:nth-of-type(2)": {
                                                fontSize: 16,
                                                color: "#1976d2",
                                            },
                                        }}
                                        id={"testType-" + 1}
                                    />
                                }
                                size="small"
                                label={parametricTestButton.elementLabel}
                                className="radio-label"
                                disabled={isParametricButtonDisabled}
                            />
                        </div>
                    )}
                    {isNonparametrictestTypeShown && (
                        <div
                            key={2}
                            style={{ width: "30%" }}
                        >
                            <div
                                style={{ width: "100%" }}
                                className={isNonParametricButtonDisabled ? "test-type test-type-disabled" : "test-type"}
                                ref={(element) => (itemEls.current[2] = element)}
                                disabled={isNonParametricButtonDisabled}
                            >
                                {nonParametricTestButton.label}
                            </div>
                            <FormControlLabel
                                sx={{
                                    "& .MuiTypography-root": {
                                        fontSize: 12,
                                        color: "#969696",
                                    },
                                }}
                                value={nonParametricTestButton.elementValue}
                                control={
                                    <Radio
                                        onChange={(e) => handleInputChange(e, "testType")}
                                        value={nonParametricTestButton.value}
                                        checked={(moduleName === "fragranceAssociation" && tTestSelections?.[selectedSection]?.["test_id"] == nonParametricTestButton.value) || checkedTestTypeForTest == nonParametricTestButton.value}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 16,
                                                color: "#969696",
                                            },
                                            "& .MuiSvgIcon-root:nth-of-type(2)": {
                                                fontSize: 16,
                                                color: "#1976d2",
                                            },
                                        }}
                                        id={"testType-" + 2}
                                    />
                                }
                                size="small"
                                label={nonParametricTestButton.elementLabel}
                                className="radio-label"
                                disabled={isNonParametricButtonDisabled}
                            />
                        </div>
                    )}
                </RadioGroup>
            </FormControl>
        );
    };

    const renderFisherLSDOptions = () => {
        if (isFragranceAssociationSection && selectedSection === "fragranceAssociation" && (tTestSelections?.["fragranceAssociation"]?.["test_id"] == 4 || checkedTestTypeForTest === 4)) {
            return (
                <FormControl sx={{ width: "100%", mt: 2 }}>
                    <FormLabel
                        id="fisher-lsd-options-label"
                        className="input-label-dialog"
                    >
                        Do you want to perform the Fisher’s LSD pairwise comparisons (after data transformation) ‘Within Product Attributes’ or ‘Across Products’?
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="fisher-lsd-options-label"
                        name="fisher-lsd-options-group"
                        value={fisherLSDComparisonType}
                        sx={{
                            gap: "24px",
                            "& .MuiFormControlLabel-label": {
                                whiteSpace: "nowrap",
                            },
                        }}
                    >
                        {returnRadioButton("Within", "Within Product Attributes", "fisherLSDType", fisherLSDComparisonType)}
                        {returnRadioButton("Across", "Across Products", "fisherLSDType", fisherLSDComparisonType)}
                    </RadioGroup>
                </FormControl>
            );
        }
        return null;
    };

    const renderZTestOptions = () => {
        if (isFragranceAssociationSection && selectedSection === "fragranceAssociation" && (tTestSelections?.["fragranceAssociation"]?.["test_id"] == 6 || checkedTestTypeForTest === 6)) {
            return (
                <FormControl sx={{ width: "100%", mt: 2 }}>
                    <FormLabel
                        id="z-test-options-label"
                        className="input-label-dialog"
                    >
                        Do you want to perform the Z-test ‘Within Product Attributes’ or ‘Across Products’?
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="z-test-options-label"
                        name="z-test-options-group"
                        value={zTestComparisonType}
                        sx={{
                            gap: "24px",
                            "& .MuiFormControlLabel-label": {
                                whiteSpace: "nowrap",
                            },
                        }}
                    >
                        {returnRadioButton("Within", "Within Product Attributes", "zTestType", zTestComparisonType)}
                        {returnRadioButton("Across", "Across Products", "zTestType", zTestComparisonType)}
                    </RadioGroup>
                </FormControl>
            );
        }
        return null;
    };

    const renderConfidenceInterval = () => {
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                >
                    Confidence Interval
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={checkedConfidenceIntervalForTest}
                >
                    {filteredConfidenceIntervals.map((ele) => {
                        const disabled = ele === 0.8 ? is1TailedDisabled : false;
                        return returnRadioButton(ele, `${ele * 100}% ${ele === 0.8 ? "(1-tailed)" : ""}`, "confidenceInterval", checkedConfidenceIntervalForTest, disabled);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    const renderDataType = () => {
        const testNameInLabel = analysisTypeValue.value === "box_analysis" ? "Z-test" : "T-test";
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                >
                    {`Do you want to perform the ${testNameInLabel} on Recall or ITM data?`}
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                >
                    {itmRecallTTestType.map((ele) => {
                        return returnRadioButton(ele, ele, "dataType", checkedDataTypeForTest);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    const getJarSelections = (backendVariable, selectionType) => {
        let obj = {};
        if (selectionType === "Top 2 Box") {
            obj[backendVariable] = { top: 2 };
        } else if (selectionType === "Bottom 2 Box") {
            obj[backendVariable] = { bottom: 2 };
        } else if (selectionType === "Middle Box") {
            obj[backendVariable] = { middle_box: true };
        } else {
            obj[backendVariable] = { top: 2, bottom: 2, middle_box: true };
        }
        return obj;
    };
    const renderJarOptions = () => {
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                >
                    Do you want to perform the Z-Test on Top 2 Box, Middle Box or Bottom 2 Box Analysis? <span style={{ fontWeight: 700 }}>(JAR)</span>
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={checkedJarBoxAnalysisType}
                >
                    {jarBoxAnalysisTTestType.map((ele) => {
                        return returnRadioButton(ele, ele, "jarBoxAnalysisType", checkedJarBoxAnalysisType);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    const renderBoxAnalysisType = () => {
        return (
            <FormControl sx={{ width: "100%" }}>
                <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    className="input-label-dialog"
                >
                    Do you want to perform the Z-test on Top Box, Bottom Box or Both?
                </FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={checkedBoxAnalysisType}
                >
                    {boxAnalysisTTestType.map((ele) => {
                        return returnRadioButton(ele, ele, "boxAnalysisType", checkedBoxAnalysisType);
                    })}
                </RadioGroup>
            </FormControl>
        );
    };

    const getProductLabel = (product, noDataAvailable) => {
        const proTitle = `${product.title}__${product.coreDataId}`;
        const proTestCode = productsTestCodeMapData[proTitle];
        const isHidden = productColumnVisibility[`checked--${product.title.split(" ").join("_")}__${product.coreDataId}`];
        const hasProTestCode = product.title.includes(proTestCode);
        const isDisabled = checkProductDisabled(product); // Check if product is disabled

        const studyTitle = selectedStudy.find((datum) => datum.id === product.coreDataId)?.study_reference__title;

        let contentTitle = hasProTestCode ? product.title.split("/")[0] : product.title;

        return (
            <span>
                {contentTitle} {moduleName !== "productPreference" && "/ "}
                {isOralCare && (
                    <>
                        <span style={!isHidden && !isDisabled ? { color: "#6f42c1" } : { color: "#969696" }}>{proTestCode}</span> /
                    </>
                )}
                {moduleName !== "productPreference" && studyTitle}
                {isHidden && <span> (Hidden by User)</span>}
                {noDataAvailable && <span> (No Data Available)</span>}
            </span>
        );
    };

    const checkProductDisabled = (product) => {
        if (isFragranceAssociationSection && selectedSection === "fragranceAssociation" && checkedTestTypeForTest === 7) {
            if (checkedProductsForTest.length >= 2) {
                const isAlreadyChecked = checkedProductsForTest.some((p) => p.title === product.title && p.coreDataId === product.coreDataId);
                if (!isAlreadyChecked) {
                    return true;
                }
            }
        }

        const keySource =
            moduleName === "productPreference"
                ? product.backendVariable
                : product.title;

        const key = `checked--${keySource.split(" ").join("_")}__${product.coreDataId}`;
        const isHidden = productColumnVisibility?.[key] === true;

        let disabled;
        if (isHidden || product.sampleSize === 0) {
            disabled = true;
        } else if (selectedProductCoreDataId === 0 || product.coreDataId === selectedProductCoreDataId) {
            disabled = false;
        } else {
            disabled = true;
        }
        const studyTypeMethod = selectedStudy[0].study_type_method__title;
        if (moduleName !== "fragranceAssociation" && moduleName !== "productPreference") {
            if (!disabled && checkedProductsForTest.length >= 2 && isPercentageValues && moduleName !== "preference" && (checkedTestTypeForTest === 6 || !(studyTypeMethod === "CLT" && checkedTestTypeForTest === 6 && (moduleName === "overall" || moduleName === "secondary")))) {
                disabled = !checkedProductsForTest.some((ele) => ele.title === product.title && ele.coreDataId === product.coreDataId);
            }
        }
        return Boolean(disabled);
    };
    const isAllowedByTestType = (product) => {
        if (moduleName !== "productPreference") return true;
        const isTwoProduct = product.products.length === 2;
        if (!(isTwoProduct ? showTwoProduct : showMultipleProduct)) return false;
        // Z Test works for both 2-product and multi-product rows, so no row is excluded
        if (checkedTestTypeForTest === 6) return true;
        if (checkedProductsForTest.length > 0) {
            return (checkedProductsForTest[0].products.length === 2) === isTwoProduct;
        }
        return true;
    };

    const checkGroups = (group) => {
        return !checkedProductsForTest.some((p) => group.some((g) => g.coreDataId === p.coreDataId));
    };

    const enableProduct = (product) => {
        if (moduleName === "fragranceAssociation") {
            const productsForTest = tTestSelections?.[selectedSection]?.products;
            return productsForTest?.some((p) => p.title === product.title && p.coreDataId === product.coreDataId) || false;
        } else {
            return checkedProductsForTest.some((p) => p.title === product.title && p.coreDataId === product.coreDataId);
        }
    };

    const handleAllClicked = (areAllEnabledProductsSelected, group) => {
        if (areAllEnabledProductsSelected) {
            const newCheckedProducts = checkedProductsForTest.filter((p) => !group.some((g) => g.title === p.title && g.coreDataId === p.coreDataId));
            setCheckedProductsForTest(newCheckedProducts);
            setTTestWarningFlag(getTTestFilterWarningFlag(newCheckedProducts, consumerFilterStoreData));
            checkSampleSizeWarning(newCheckedProducts);

            if (moduleName === "fragranceAssociation") {
                let tempObj = { ...tTestSelections };
                tempObj[selectedSection]["products"] = newCheckedProducts;
                setTTestSelections({ ...tempObj })
            }

        } else {
            const productsToAdd = group.filter((product) => !checkProductDisabled(product) && isAllowedByTestType(product) && !checkedProductsForTest.some((p) => p.title === product.title && p.coreDataId === product.coreDataId));
            const studyTypeMethod = selectedStudy[0].study_type_method__title;
            let newCheckedProducts = [...checkedProductsForTest, ...productsToAdd];
            const modulesWithTwoProductLimit = ["agreement", "overall", "secondary", "claims", "justRight"];
            if ((modulesWithTwoProductLimit.includes(moduleName) && isPercentageValues) || (isFragranceAssociationSection && selectedSection === "fragranceAssociation" && checkedTestTypeForTest === 7) || (studyTypeMethod === "Express CLT" && isPercentageValues && moduleName !== "productPreference")) {
                newCheckedProducts = [...newCheckedProducts.slice(0, 2)];
            }
            setCheckedProductsForTest(newCheckedProducts);
            setTTestWarningFlag(getTTestFilterWarningFlag(newCheckedProducts, consumerFilterStoreData));
            checkSampleSizeWarning(newCheckedProducts);

            if (moduleName === "fragranceAssociation") {
                let tempObj = { ...tTestSelections };
                tempObj[selectedSection]["products"] = newCheckedProducts;
                setTTestSelections({ ...tempObj })
            }
        }
    };

    const checkSameProductCount = (product, group) => {
        if (checkedTestTypeForTest === 6) return true; // Z Test can mix 2-product and multi-product rows
        const productLengthArr = group.map((ele) => ele.products.length);
        if ( productLengthArr.includes(2) && checkedProductsForTest.length > 0) {
            const checkIfSameCount = checkedProductsForTest[0].products.length === product.products.length;
            return checkIfSameCount;
        } else {
            return true;
        }
    };

    const getProductScoresForKpis = (product) => {
        let dataMappings = [];
        let fragranceProduct = {};
        if (moduleName === "fragranceAssociation") {
            if(selectedSection === "fragranceAssociation") {
                dataMappings = fragranceAssociationMappings.flatMap(ele => ele.displayLabels).map(ele => ele.backendVariable);
                fragranceProduct = fragranceAssociationDataState.find(ele => ele.title === product.title && ele.coreDataId === product.coreDataId);
            } else {
                dataMappings = mappings.flatMap(ele => ele.displayLabels || [ele]).map(item => item.backendVariable);
            }
        } else {
            dataMappings = mappings.flatMap(ele => ele.displayLabels || [ele]).map(item => item.backendVariable);
        } 
        if (product.scores.length === 0) {
            return true;
        } else {
            let scoreKey = "";
            if (analysisTypeValue.value === "mean") {
                scoreKey = "mean";
            } else if (analysisTypeValue.value === "count") {
                if (moduleName === "fragranceAssociation" && selectedSection === "overall") {
                    scoreKey = "mean";
                } else {
                    scoreKey = "count"
                }
            } else if (analysisTypeValue.value === "percentage") {
                if (moduleName === "fragranceAssociation" && selectedSection === "overall") {
                    scoreKey = "mean";
                } else {
                    scoreKey = "percentage";
                }
            } else {
                scoreKey = boxAnalysisTypeSelection .includes("Top Box") ? "top_per" : boxAnalysisTypeSelection.includes("Bottom Box") ? "bottom_per" : "middle_per";
            }
            let check = false;
            if (moduleName === "fragranceAssociation" && selectedSection === "fragranceAssociation") {
                const checkAttributeValues = fragranceProduct.scores.some(score => score.attribute_values.some(attr => attr.count !== null || attr.percentage !== null));
                check = fragranceProduct.scores.some(score => dataMappings.includes(score.title) && score.attribute_values.length > 0 && checkAttributeValues);
            } else {
                check = product.scores.some(score => dataMappings.includes(score.backendVariable) && score[scoreKey] != null && score[scoreKey] !== undefined);
            }
            // console.log("check", check, scoreKey);
            return !check;
        }
    }

    const returnCheckboxGroup = (group, isGroupDisabled, dataPresent) => {
        const checkBoxGroup = group.map((product, index) => {
            const disabled = checkProductDisabled(product) || isGroupDisabled || dataPresent;
            const isChecked = enableProduct(product);
            const fcColor = isChecked ? "theme.palette.primary.main" : "theme.palette.text.primary";
            const fcBgColor = isChecked ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none";
            const disableCheckBox = moduleName === "productPreference" ? !checkSameProductCount(product, group) : getProductScoresForKpis(product);
            let disableCheckBoxByTesttype = false;
            if (moduleName === "productPreference") {
                if (product.products.length === 2) {
                    if (showTwoProduct) {
                        disableCheckBoxByTesttype = false;

                    } else {
                        disableCheckBoxByTesttype = true;
                    }
                } else {
                    if (showMultipleProduct) {
                        disableCheckBoxByTesttype = false;

                    } else {
                        disableCheckBoxByTesttype = true;
                    }
                }
            }

            return (
                <FormControlLabel
                    sx={{
                        "& .MuiTypography-root": {
                            fontSize: 12,
                            color: "#495057",
                            padding: 0,
                        },
                        borderRadius: "5px",
                        margin: "3px 0px",
                        padding: "0px",
                        color: fcColor,
                        background: fcBgColor,
                    }}
                    control={
                        <Checkbox
                            sx={{
                                color: "#969696",
                                "& .MuiSvgIcon-root": {
                                    fontSize: 16,
                                },
                                "& Mui-checked .MuiSvgIcon-root": {
                                    fontSize: 16,
                                    color: "#1976d2",
                                },
                                padding: "6px",
                            }}
                            checked={isChecked}
                            value={product.title}
                            disabled={disabled || disableCheckBox || disableCheckBoxByTesttype}
                            name={product.title}
                            onChange={(e) => handleInputChange(e, "products", product)}
                        />
                    }
                    label={getProductLabel(product, dataPresent || disableCheckBox)}
                    key={index + 1}
                />
            );
        });
        return checkBoxGroup;
    };

    useEffect(() => {
        setLoader(true);
        let tempArr = [...checkAvailibility];
        selectedStudy.forEach((datum) => {
            const studyTypeMethod = datum?.study_type_method;
            const id = datum?.id;
            const pushedStudy = checkAvailibility.some(ele => ele.coreDataId === id);
            if (!pushedStudy) {
                try {
                    let url = process.env.REACT_APP_SERVER_PATH + `/core-data-models/navigation-availability/?core_data_ids=${id}`;
                    if (productCategoryId) {
                        url += `&product_category=${productCategoryId}`;
                    }
                    if (studyTypeMethod) {
                        url += `&study_type_method=${studyTypeMethod}`;
                    }
                    api
                        .get(url)
                        .then((res) => {
                            tempArr.push({ coreDataId: id, isAvailable: res.data?.[moduleNameForResponse[moduleName + module]] });
                            setLoader(false);
                        })
                        .catch((error) => {
                            handleError("error", error);
                            setLoader(false);
                        });
                } catch (error) {
                    handleError("error", error);
                    setLoader(false);
                }
            }
        })
        setCheckAvailability(tempArr)
    }, [selectedStudy, productCategoryId, moduleName]);

    const hasSameProductCountInGroup = (group) => {
        if (checkedTestTypeForTest === 6) return true; // Z Test can mix 2-product and multi-product rows
        const allowedRows = group.filter(isAllowedByTestType);
        if (allowedRows.length === 0) return group.length === 0;
        const productLengthArr = allowedRows.map((ele) => ele.products.length);
        if (productLengthArr.includes(2)) {
            const firstLength = allowedRows[0].products.length;
            return allowedRows.every((obj) => obj.products.length === firstLength);
        }
        return true;
    };

    const renderGroups = (productGroups, hasMultipleStudies) => {
        const groups = productGroups.map((group, groupIndex) => {
            const coreDataId = group?.[0].coreDataId;
            const isDataPresent = checkAvailibility.find(ele => ele.coreDataId === coreDataId);
            const isGroupDisabled = (hasMultipleStudies && checkedProductsForTest.length > 0 && checkGroups(group)) || !isDataPresent["isAvailable"];
            const key = `group-${groupIndex}`;
            const enabledProducts = group.filter((p) => !checkProductDisabled(p) && isAllowedByTestType(p));
            const areAllEnabledProductsSelected = enabledProducts.length > 0 && enabledProducts.every((product) => enableProduct(product));
            const disableSelectAll = moduleName === "productPreference" ? !hasSameProductCountInGroup(group) : false;

            return (
                <div
                    key={key}
                    style={{ marginBottom: "15px" }}
                >
                    <FormControlLabel
                        sx={{
                            width: "100%",
                            "& .MuiTypography-root": {
                                fontSize: 12,
                                color: "#495057",
                                fontWeight: "bold",
                            },
                            margin: "3px 0px",
                            marginLeft: "-9px",
                            borderRadius: "5px",
                            background: areAllEnabledProductsSelected ? "linear-gradient(270deg, rgb(0 103 239 / 0%) 0%, rgb(0 56 139 / 9%) 78%)" : "none",
                        }}
                        control={
                            <Checkbox
                                sx={{
                                    color: isGroupDisabled ? "#ccc" : "#969696",
                                    "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                    },
                                    "& Mui-checked .MuiSvgIcon-root": {
                                        fontSize: 16,
                                        color: "#1976d2",
                                    },
                                    padding: "6px",
                                }}
                                checked={areAllEnabledProductsSelected}
                                onChange={() => handleAllClicked(areAllEnabledProductsSelected, group)}
                                disabled={isGroupDisabled || disableSelectAll}
                            />
                        }
                        label="Select All"
                    />

                    <FormGroup sx={{ width: "100%" }}>{returnCheckboxGroup(group, isGroupDisabled, !isDataPresent["isAvailable"])}</FormGroup>
                    {hasMultipleStudies && groupIndex < productGroups.length - 1 && (
                        <div
                            style={{
                                height: "1px",
                                backgroundColor: "#e0e0e0",
                                margin: "10px 0",
                            }}
                        />
                    )}
                </div>
            );
        });

        return groups;
    };

    const renderProducts = () => {
        const sortedProducts = SortFinalData(productsSecondaryDataState, productsOrder);
        const hasMultipleStudies = selectedStudy.length > 1;
        let productGroups = [];
        if (hasMultipleStudies) {
            const groupedProducts = {};
            sortedProducts.forEach((product) => {
                const studyId = product.coreDataId;
                if (!groupedProducts[studyId]) {
                    groupedProducts[studyId] = [];
                }
                groupedProducts[studyId].push(product);
            });
            productGroups = Object.values(groupedProducts);
            totalProducts = productGroups.flat().length;
        } else {
            productGroups = [sortedProducts];
            totalProducts = productsSecondaryDataState.length;
        }

        return (
            <FormControl
                component="fieldset"
                variant="standard"
                sx={{ width: "100%" }}
            >
                <FormLabel
                    component="legend"
                    className="input-label-dialog mb-0"
                >
                    Products
                </FormLabel>

                {renderGroups(productGroups, hasMultipleStudies)}
            </FormControl>
        );
    };

    const getTestName = () => {
        // student t-test for 80% confidence interval
        if (checkedTestTypeForTest < 4) {
            if (checkedTestTypeForTest === 1 && checkedConfidenceIntervalForTest === "0.8") {
                return "Student's T Test Comparisons(1-tailed)";
            }
            return items[checkedTestTypeForTest].elementValue;
        }

        if (checkedTestTypeForTest === 5) {
            return "Post-hoc Dunn Test";
        }
        // set name for z-test
        if (checkedTestTypeForTest === 6) {
            if (moduleName === "preference" && checkedProductsForTest.length >= 3) {
                return "z test multiple_product";
            } else if (moduleName === "preference" && checkedProductsForTest.length === 2) {
                return "preference_analysis z test";
            } else if (moduleName === "productPreference") {
                const isMultiProductTest = checkedProductsForTest.some(
                    (product) => product.title.split("vs").length > 2
                );
                if (isMultiProductTest) {
                    return "product_preference z test multiple_product";
                } else {
                    return "product_preference z test";
                }
            } else if (moduleName === "fragranceAssociation") {
                return "fragrance_association z test";
            } else {
                return "Z Test";
            }
        }
        // binomial
        if (checkedTestTypeForTest === 7 && isFragranceAssociationSection && selectedSection === "fragranceAssociation") {
            return "fragrance_association binomial test";
        } else if (checkedTestTypeForTest === 7 && moduleName !== "productPreference") {
            return "non-parametric binomial test";
        } else if (checkedTestTypeForTest === 7 && moduleName === "productPreference") {
            return "product_preference binomial test";
        }
        // fisher's lsd
        if (checkedTestTypeForTest === 4 && moduleName === "fragranceMetrics") {
            return "fragrance fisher's lsd pairwise comparisons";
        } else if (checkedTestTypeForTest === 4 && moduleName === "preference") {
            return "preference_analysis fisher's lsd";
        } else if (checkedTestTypeForTest === 4 && moduleName === "productPreference") {
            return "product_preference fisher's lsd";
        } else if (checkedTestTypeForTest === 4 && isFragranceAssociationSection && selectedSection === "fragranceAssociation") {
            return "fragrance_association fisher's lsd";
        } else if (checkedTestTypeForTest === 4 && selectedSection === "overall") {
            return "fisher's lsd pairwise comparisons";
        } else {
            return "Fisher's LSD pairwise comparisons";
        }
    };

    const getTestNameById = (testId) => {
        // student t-test for 80% confidence interval        
        if (testId === 0) {
            return "No T-Test";
        } else if (testId === 1) {
            return "Student's T Test Comparisons";
        } else if (testId === 2) {
            return "Kruskall Wallis with post-hoc Dunn Test"
        } else if (testId === 5) {
            return "Post-hoc Dunn Test";
        } else if (testId === 6 && selectedSection === "fragranceAssociation") {
            return "fragrance_association z test";
        } else if (testId === 6 && selectedSection === "overall") {
            return "Z Test";
        } else if (testId === 7) {
            // binomial
            return "fragrance_association binomial test";
        } else if (testId === 4 && selectedSection === "fragranceAssociation") {
            return "fragrance_association fisher's lsd";
        } else if (testId === 4 && selectedSection === "overall") {
            return "fisher's lsd pairwise comparisons";
        }
    };

    const updateSignificanceToggleState = () => {
        const studyTypeMethod = selectedStudy[0].study_type_method__title;

        if (moduleName === "fragranceAssociation") {
            let overallFlag = true;
            let faFlag = true;
            const overallSelection = tTestSelections?.overall;
            const overallProducts = overallSelection?.products || [];
            if (overallProducts.length === 2) {
                overallFlag = false;
            } else {
                overallFlag = true;
            }
            const faSelection = tTestSelections?.fragranceAssociation;
            const testId = faSelection?.test_id;
            const products = faSelection?.products || [];
            const comparisonType = faSelection?.within_product_text;

            if (parseInt(testId) === 6 && comparisonType === "Across" && products.length === 2 || parseInt(testId) === 7) {
                faFlag = false;
            } else {
                faFlag = true;
            }
            const isSignificanceDisable = overallFlag && faFlag
            dispatch(loadSignificanceToggleState(!isSignificanceDisable));
            dispatch(loadDisableSignificanceToggleState(isSignificanceDisable));
        } else if ((moduleName === "overall" || moduleName === "secondary" || moduleName === "agreement" || moduleName === "fragranceMetrics" || moduleName === "preference") && (studyTypeMethod === "CLT" || studyTypeMethod === "Express CLT" || studyTypeMethod === "ICT")) {
            const flag = checkedProductsForTest.length === 2;
            dispatch(loadSignificanceToggleState(flag));
            dispatch(loadDisableSignificanceToggleState(!flag));
        } else {
            dispatch(loadSignificanceToggleState(false));
            dispatch(loadDisableSignificanceToggleState(true));
        }
    };

    const getVariableCategory = () => {
        if (analysisTypeValue.value == "box_analysis" && moduleName === "secondary" && variableCategory === "Fragrance Metrics Analysis") {
            return `${variableCategory},JAR`;
        } else if (variableCategory === "Fragrance Association" && selectedSection === "overall") {
            return `Fragrance Metrics Analysis`;
        } else {
            return variableCategory;
        }
    };

    const getTTestResult = async () => {
        // get data view and store in redux on what key actually the test has been performed
        const _dataView = getDataView(analysisTypeValue, weightToggle, boxAnalysisTypeSelection);
        let dataView = _dataView;
        if (checkedBoxAnalysisType == boxAnalysisTTestType[0]) {
            dataView = _dataView.split(",")[0];
        } else if (checkedBoxAnalysisType == boxAnalysisTTestType[1]) {
            dataView = _dataView.split(",")[1];
        }

        setIsTTestLoading(true);
        const testName = getTestName();
        let products = [];
        let comparisons = {};
        if (moduleName === "productPreference") {
            const tempProducts = checkedProductsForTest.flatMap((product) => product.products);
            products = [...new Set(tempProducts)];
            // products = checkedProductsForTest.map(ele => ele.backendVariable);
            checkedProductsForTest.forEach((ele) => {
                comparisons[ele.backendVariable] = ele.products;
            });
        } else {
            products = checkedProductsForTest.map((product) => product.title);
        }

        const coreDataModelId = checkedProductsForTest[0].coreDataId.toString();
        const studyType = checkedProductsForTest[0].studyType.toString();
        const studyTypeMethod = checkedProductsForTest[0].studyTypeMethod.toString();
        let _consumer_filters = [];
        let result = [];

        checkedProductsForTest.forEach((datum) => {
            if (moduleName === "productPreference") {
                const tempProducts = datum?.products || [];
                tempProducts.forEach((ele) => {
                    const product = ele.split(" ").filter(item => item != "").join("_");
                    let _tempFilters = consumerFilterPayload(consumerFilterStoreData, product, datum.coreDataId);
                    if (_tempFilters && Object.keys(_tempFilters).length > 0) {
                        _tempFilters["core_data_model_id"] = datum.coreDataId;
                        _tempFilters["product_code"] = ele;
                        if (!_consumer_filters.some(item => JSON.stringify(item) === JSON.stringify(_tempFilters))) {
                            _consumer_filters.push(JSON.parse(JSON.stringify(_tempFilters)));
                        }                        
                    }
                })
            } else {
                const product = datum.title.split(" ").filter(item => item != "").join("_");
                let _tempFilters = consumerFilterPayload(consumerFilterStoreData, product, datum.coreDataId);
                if (_tempFilters && Object.keys(_tempFilters).length > 0) {
                    _tempFilters["core_data_model_id"] = datum.coreDataId;
                    _tempFilters["product_code"] = datum.title;
                    _consumer_filters.push(JSON.parse(JSON.stringify(_tempFilters)));
                }
            }
        });
        let boxAnalysis = analysisTypeValue.value === "box_analysis" && checkedBoxAnalysisType.toLowerCase() === "both" ? generateBoxAnalysisPayload(boxAnalysisSelection) : generateBoxAnalysisPayloadBySelection(boxAnalysisSelection, checkedBoxAnalysisType?.toLowerCase()?.split(" ")?.[0]) ?? {};
        if (moduleName === "secondary" && selectedStudy[0].study_type_method__title === "CLT") {
            const tempObj = getJarSelections("R_F_Intensity(JAR)", checkedJarBoxAnalysisType);
            Object.assign(boxAnalysis, tempObj);
            dispatch(loadTTestJarAnalysisType(checkedJarBoxAnalysisType));
        }
        let tTestAnalysisType;
        if (checkedBoxAnalysisType?.toLowerCase() === "both") {
            tTestAnalysisType = "top,bottom";
        } else if (checkedBoxAnalysisType) {
            tTestAnalysisType = checkedBoxAnalysisType.toLowerCase().split(" ")[0];
        } else {
            tTestAnalysisType = "mean";
        }

        const productCodes = checkedProductsForTest
            ?.map((product) => {
                return product.title;
            })
            .join(",");
        dispatch(loadTTestSelectedProducts(checkedProductsForTest));
        dispatch(loadTTestPerformed(true));
        dispatch(loadTTestType(testName));
        dispatch(loadTTestConfidenceInterval(checkedConfidenceIntervalForTest));
        dispatch(loadTTestProducts(productCodes));
        dispatch(loadTTestCoreDataId(checkedProductsForTest[0].coreDataId));
        dispatch(loadTTestCheckedDataTypeForTest(checkedDataTypeForTest)); // itm , recall, both
        dispatch(loadTTestAnalysisType(tTestAnalysisType)); // top, bottom, mean
        dispatch(loadTestAnalysisType(dataView)); // key on which data is been performed
        dispatch(loadTTestBoxAnalysis(boxAnalysis));

        try {
            let payload = {
                test_details: {
                    core_data_model_id: coreDataModelId,
                    study_type: studyType,
                    study_type_method: studyTypeMethod,
                    consumer_filters: _consumer_filters,
                    variable_category: getVariableCategory(),
                    products: products,
                    test_name: testName,
                    confidence_interval: checkedConfidenceIntervalForTest * 100,
                    weight_toggle: weightToggle,
                    product_category: productCategoryId,
                    box_analysis: boxAnalysis,
                },
            };

            if (moduleName === "productPreference") {
                payload.test_details.comparisons = comparisons;
            }
            if (moduleName === "fragranceAssociation") {
                if (testName === "fragrance_association fisher's lsd") {
                    payload.test_details.within_product = fisherLSDComparisonType === "Within";
                }

                if (testName === "fragrance_association z test") {
                    payload.test_details.within_product = zTestComparisonType === "Within";
                }

                if (testName === "student's t test comparisons" || testName === "fisher's lsd pairwise comparisons" || testName === "kruskall wallis with post-hoc dunn test") {
                    payload.test_details.overall_fragrance = true;
                }
            }
            if (isITMDataShown) {
                payload.test_details.data_type = checkedDataTypeForTest;
                if (checkedDataTypeForTest === "ITM") {
                    payload.test_details.variable_category = "KPI ITM";
                } else if (checkedDataTypeForTest === "Recall") {
                    payload.test_details.variable_category = "KPI (OO)";
                } else {
                    payload.test_details.variable_category = "KPI (OO),KPI ITM";
                }
            }
            if (testName === "Fisher's LSD pairwise comparisons") {
                setIsFisher(true);
            } else {
                setIsFisher(false);
            }
            const urlPath = process.env.REACT_APP_SERVER_PATH + `/secondary-study-datas/statistical-tests/`;
            // Z Test on a mix of 2-product and multi-product rows: each kind goes under its own test name
            const isMixedZTest = moduleName === "productPreference" && testName === "product_preference z test multiple_product" && Object.values(comparisons).some((rowProducts) => rowProducts.length === 2);
            let requestPayloads = [payload];
            if (isMixedZTest) {
                // Multi-product first: the page reads result[0] to know which products the test covers
                requestPayloads = [
                    { name: testName, isKind: (rowProducts) => rowProducts.length !== 2 },
                    { name: "product_preference z test", isKind: (rowProducts) => rowProducts.length === 2 },
                ].map(({ name, isKind }) => {
                    const kindComparisons = Object.fromEntries(Object.entries(comparisons).filter(([, rowProducts]) => isKind(rowProducts)));
                    return {
                        test_details: {
                            ...payload.test_details,
                            test_name: name,
                            comparisons: kindComparisons,
                            products: [...new Set(Object.values(kindComparisons).flat())],
                        },
                    };
                });
            }
            await Promise.all(requestPayloads.map((requestPayload) => api.post(urlPath, requestPayload)))
                .then((responses) => {
                    result = responses.length === 1 ? responses[0].data : responses.flatMap((res) => res.data);
                    setIsTTestLoading(false);
                    updateSignificanceToggleState();
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsTTestLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsTTestLoading(false);
        }
        return result;
    };

    const getFragranceAssociationTTestResult = async () => {
        setIsTTestLoading(true);
        let result = [];
        try {
            const urlPath = process.env.REACT_APP_SERVER_PATH + `/secondary-study-datas/statistical-tests/`;
            const requests = Object.keys(tTestSelections)
                .filter((key) => {
                    const value = tTestSelections[key];
                    return value && Object.keys(value).length > 0;
                })
                .map(async (datum) => {
                    const coreDataModelId = tTestSelections[datum]["products"][0]["coreDataId"];
                    const studyType = tTestSelections[datum]["products"][0]["studyType"];
                    const studyTypeMethod = tTestSelections[datum]["products"][0]["studyTypeMethod"];
                    const testName = tTestSelections[datum].test_name;
                    let _consumer_filters = [];
                    let _products = [];

                    tTestSelections[datum]["products"].forEach((datum) => {
                        _products.push(datum.title);
                        const product = datum.title.split(" ").filter(item => item != "").join("_");
                        let _tempFilters = consumerFilterPayload(consumerFilterStoreData, product, datum.coreDataId);
                        if (_tempFilters && Object.keys(_tempFilters).length > 0) {
                            _tempFilters["core_data_model_id"] = datum.coreDataId;
                            _tempFilters["product_code"] = datum.title;
                            _consumer_filters.push(JSON.parse(JSON.stringify(_tempFilters)));
                        }
                    });

                    dispatch(loadTTestPerformed(true));
                    dispatch(loadTTestCoreDataId(checkedProductsForTest[0].coreDataId));
                    dispatch(loadTTestSelections(tTestSelections));

                    let payload = {
                        test_details: {
                            core_data_model_id: coreDataModelId,
                            study_type: studyType,
                            study_type_method: studyTypeMethod,
                            consumer_filters: _consumer_filters,
                            variable_category: datum === "overall" ? "Fragrance Metrics Analysis" : "Fragrance Association",
                            products: _products,
                            test_name: testName,
                            confidence_interval: parseFloat(tTestSelections[datum].confidence_interval) * 100,
                            weight_toggle: false,
                            product_category: productCategoryId,
                            box_analysis: {},
                        },
                    };

                    if (testName === "fragrance_association fisher's lsd" || testName === "fragrance_association z test") {
                        payload.test_details.within_product = tTestSelections[datum].within_product_text === "Within";
                    }

                    if (testName === "student's t test comparisons" || testName === "fisher's lsd pairwise comparisons" || testName === "kruskall wallis with post-hoc dunn test") {
                        payload.test_details.overall_fragrance = true;
                    }

                    return api.post(urlPath, payload);
                });

            await api
                .all(requests)
                .then(async (res) => {
                    result = res.map((re) => re.data).flat();
                    updateSignificanceToggleState();
                    setIsTTestLoading(false);
                })
                .catch((error) => {
                    handleError("error", error);
                    setIsTTestLoading(false);
                });
        } catch (error) {
            handleError("error", error);
            setIsTTestLoading(false);
        }

        return result;
    };

    // T Test dialogue function
    const handleTTestDialogueSubmit = async () => {
        setShowDialogSampleWarning(false);
        if (moduleName === "productPreference" && checkedProductsForTest.length > 0) {
            setLegends(checkedProductsForTest[0].scores);
        } else {
            setLegends(checkedProductsForTest);
        }

        if (checkedTestTypeForTest === 0) {
            clearResult();
            handleTTestDialogueClose();
            return;
        }

        let _result = [];
        try {
            const confidenceIntervalPercentage = Number(checkedConfidenceIntervalForTest) * 100;
            const isOneTailed = confidenceIntervalPercentage === 80;
            if (checkedTestTypeForTest === 1 && checkedProductsForTest.length === 2) {
                _result = await getTTestResult();
                setTestLabel(`A Student's T-Test with ${confidenceIntervalPercentage}%${isOneTailed ? " (1-tailed)" : ""} confidence interval was performed.`);
            } else if (checkedTestTypeForTest === 4 && checkedProductsForTest.length >= 1 && isFragranceAssociationSection) {
                _result = await getTTestResult();
                const comparisonText = fisherLSDComparisonType ? `(${fisherLSDComparisonType} Product Attributes)` : "";
                setTestLabel(`A Fisher's LSD Pairwise Test ${comparisonText} with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            } else if (checkedTestTypeForTest === 4 && checkedProductsForTest.length >= 3) {
                _result = await getTTestResult();
                if (moduleName === "preference" || moduleName === "productPreference") {
                    setTestLabel(`A Fisher's LSD pairwise comparisons (after data transformation) with ${confidenceIntervalPercentage}% confidence interval was performed.`);
                } else {
                    setTestLabel(`A Fisher's LSD Pairwise Test with ${confidenceIntervalPercentage}%${isOneTailed ? " (1-tailed)" : ""} confidence interval was performed.`);
                }
            } else if (checkedTestTypeForTest === 4 && checkedProductsForTest.length < 3 && (moduleName === "fragranceMetrics" || moduleName === "preference" || moduleName === "productPreference")) {
                _result = await getTTestResult();
                if (moduleName === "preference" || moduleName === "productPreference") {
                    setTestLabel(`A Fisher's LSD pairwise comparisons (after data transformation) with ${confidenceIntervalPercentage}% confidence interval was performed.`);
                } else {
                    setTestLabel(`A Fisher's LSD Pairwise Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
                }
            } else if (checkedTestTypeForTest === 2 && checkedProductsForTest.length === 2) {
                _result = await getTTestResult();
                setTestLabel(`A Kruskall-Wallis Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            } else if (checkedTestTypeForTest === 5 && checkedProductsForTest.length >= 3) {
                _result = await getTTestResult();
                setTestLabel(`A Post-hoc Dunn Test with ${confidenceIntervalPercentage}%${isOneTailed ? " (1-tailed)" : ""} confidence interval was performed.`);
            } else if (checkedTestTypeForTest === 6 && moduleName === "productPreference") {
                _result = await getTTestResult();
                setTestLabel(`A Z-Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            } else if (checkedTestTypeForTest === 6 && checkedProductsForTest.length >= 1) {
                _result = await getTTestResult();

                let analysisTypeText = "";
                if (checkedBoxAnalysisType === "Top Box") {
                    analysisTypeText = "Top Box";
                } else if (checkedBoxAnalysisType === "Bottom Box") {
                    analysisTypeText = "Bottom Box";
                } else if (checkedBoxAnalysisType === "Both") {
                    analysisTypeText = "Top & Bottom Box";
                } else if (zTestComparisonType) {
                    analysisTypeText = `${zTestComparisonType} Product Attributes`;
                }
                setTestLabel(`A Z-Test on ${analysisTypeText} with ${confidenceIntervalPercentage}%${isOneTailed ? " (1-tailed)" : ""} confidence interval was performed.`);
            } else if (checkedTestTypeForTest === 7 && checkedProductsForTest.length <= 2) {
                _result = await getTTestResult();
                setTestLabel("A Binomial (P Value Two Sided​) with " + confidenceIntervalPercentage + "% confidence interval was performed.");
            } else if (checkedTestTypeForTest === 7 && moduleName === "productPreference") {
                _result = await getTTestResult();
                setTestLabel("A Binomial (P Value Two Sided​) with " + confidenceIntervalPercentage + "% confidence interval was performed.");
            } else if (checkedTestTypeForTest === 1 && checkedProductsForTest.length > 2) {
                handleError("error", "Can't perform Student's T-Test on more that two products");
                return;
            }

            if (_result.length > 0) {
                resetTestDialogState();
                handleTTestDialogueClose();
                setTTestResult(_result);
            }
        } catch (error) {
            handleError("error", error.message);
        }
    };

    const handleFragranceAssociationTTestDialogueSubmit = async () => {
        setShowDialogSampleWarning(false);

        const overallSelection = tTestSelections?.overall;
        const overallProducts = overallSelection?.products || [];
        if (overallProducts.length === 2) {
            setLegends(overallProducts);
        } else {
            setLegends([]);
        }

        const faSelection = tTestSelections?.fragranceAssociation;
        const testId = faSelection?.test_id;
        const products = faSelection?.products || [];
        const comparisonType = faSelection?.within_product_text;
        if (parseInt(testId) === 6 && comparisonType === "Across" && products.length === 2) {
            setFaLegends(products);
            dispatch(loadTTestFaLegends(products));
        } else if (parseInt(testId) === 7 && products.length === 2) {
            setFaLegends(products);
            dispatch(loadTTestFaLegends(products));
        } else {
            setFaLegends([]);
            dispatch(loadTTestFaLegends([]));
        }

        if (parseInt(tTestSelections["overall"].test_id) === 0 || parseInt(tTestSelections["fragranceAssociation"].test_id) === 0) {
            clearResult();
            handleTTestDialogueClose();
            return;
        }

        let _result = [];
        try {
            const confidenceIntervalPercentage = Number(tTestSelections["overall"].confidence_interval) * 100;
            const confidenceIntervalPercentage2 = Number(tTestSelections["fragranceAssociation"].confidence_interval) * 100;

            _result = await getFragranceAssociationTTestResult();
            if (parseInt(tTestSelections["overall"].test_id) === 1) {
                setTestLabel(`A Student's T-Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            } else if (parseInt(tTestSelections["overall"].test_id) === 2) {
                setTestLabel(`A Kruskall-Wallis Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            } else if (parseInt(tTestSelections["overall"].test_id) === 4) {
                setTestLabel(`A Fisher's LSD Pairwise Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            } else if (parseInt(tTestSelections["overall"].test_id) === 5) {
                setTestLabel(`A Post-hoc Dunn Test with ${confidenceIntervalPercentage}% confidence interval was performed.`);
            }

            if (parseInt(tTestSelections["fragranceAssociation"].test_id) === 4) {
                setTestLabel2(`A Fisher's LSD Pairwise Comparison (after data tranformation) ${tTestSelections["fragranceAssociation"].within_product_text.toLowerCase()} ${tTestSelections["fragranceAssociation"].within_product_text.toLowerCase() === "across" ? "products" : "product attributes"} with ${confidenceIntervalPercentage2}% confidence interval was performed.`);
            } else if (parseInt(tTestSelections["fragranceAssociation"].test_id) === 6) {
                const modeText = tTestSelections["fragranceAssociation"].within_product_text.toLowerCase() === 'across' ? 'across products' : 'within product attributes';
                setTestLabel2(`A Z-Test ${modeText} with ${confidenceIntervalPercentage2}% confidence interval was performed.`);
            } else if (parseInt(tTestSelections["fragranceAssociation"].test_id) === 7) {
                setTestLabel2("A Binomial (P Value Two Sided​) with " + confidenceIntervalPercentage2 + "% confidence interval was performed.");
            }

            if (_result.length > 0) {
                resetTestDialogState();
                handleTTestDialogueClose();
                setTTestResult(_result);
            }
        } catch (error) {
            handleError("error", error.message);
        }
    };

    const resetTestDialogState = () => {
        setCheckedConfidenceIntervalForTest("");
        setCheckedTestTypeForTest("");
        setCheckedBoxAnalysisType(boxAnalysisTypeSelection.length == 1 ? boxAnalysisTypeSelection[0] : "");
        if (isITMDataShown) setCheckedDataTypeForTest("");
        setCheckedProductsForTest([]);
        setFisherLSDComparisonType("");
        setZTestComparisonType("");
        setCheckedJarBoxAnalysisType("");
        setShowTwoProduct(true);
        setShowMultipleProduct(true);
        if (moduleName === "fragranceAssociation") {
            setTTestSelections({
                "overall": {},
                "fragranceAssociation": {}
            });
        }
    };

    const renderSectionToggle = () => {
        if (!overallToggleEnabled) {
            return null;
        }

        return (
            <div
                className="mt-3"
                style={{ display: "flex", justifyContent: "flex-start" }}
            >
                <ToggleButtonGroup
                    value={selectedSection}
                    exclusive
                    onChange={handleSectionChange}
                    aria-label="text alignment"
                    sx={{
                        "& .MuiToggleButton-root": {
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "14px",
                            padding: "5px 15px",
                            border: "1px solid #1976d2",
                            color: "#1976d2",
                        },
                        "& .Mui-selected": {
                            background: "var(--light-blue-gradient-background)",
                            color: "#e8edf3 !important",
                        },
                    }}
                >
                    <ToggleButton
                        value="overall"
                        aria-label="overall"
                    >
                        Overall
                    </ToggleButton>
                    <ToggleButton
                        value="fragranceAssociation"
                        aria-label="fragrance association"
                    >
                        Fragrance Association
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>
        );
    };

    const renderDualSelectionWarning = () => {
        if (overallToggleEnabled) {
            return (
                <div style={{ display: "flex", justifyContent: "center", paddingBottom: "15px", margin: "0px 24px", alignItems: "center" }}>
                    <WarningAmberOutlinedIcon sx={{ color: "rgb(227, 41, 41)", fontSize: 20, mr: 1 }} />
                    <p
                        style={{
                            margin: 0,
                            color: "#495057",
                            fontWeight: 600,
                            fontSize: 12,
                            textAlign: "center",
                        }}
                    >
                        The ‘Get Results’ button will be enabled only after the user selects both the Overall & Fragrance Association T‑Test type.
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {(isTTestLoading || loader) && <Loader />}
            <Button
                size="small"
                className={isTTestButtonDisabled || tTestBtnDisabled || absoluteToggle ? "custom-btn me-4 bold-content disabled" : "custom-btn liner-light-blue-background me-4 bold-content"}
                variant="contained"
                startIcon={<ScienceIcon />}
                onClick={handleTTestDialogueOpen}
                disabled={isTTestButtonDisabled || tTestBtnDisabled || absoluteToggle}
            >
                T-Test
            </Button>
            {tTestDialogueOpen && (
                <Dialog
                    open={tTestDialogueOpen}
                    onClose={handleTTestDialogueClose}
                    fullWidth={true}
                    maxWidth="md"
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 15,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                        id="alert-dialog-title"
                    >
                        Please select type of T-Test & Confidence Interval
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 2,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleTTestDialogueClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <div style={{ height: 400, overflowY: "auto", padding: "0 10px" }}>
                            {renderSectionToggle()}

                            {isFragranceAssociationSection && overallToggleEnabled && (
                                <div style={{ textAlign: "left", fontSize: "14px", color: "#333", marginTop: "15px", marginBottom: "10px" }}>
                                    {selectedSection === "overall" ? <strong>Select the T-Test for Overall Fragrance Metrics Analysis</strong> : <strong>Select the T-Test for Fragrance Association</strong>}
                                </div>
                            )}

                            <div
                                className="mt-3"
                                style={{
                                    display: "flex",
                                }}
                            >
                                {renderTestType()}
                            </div>

                            {renderFisherLSDOptions()}
                            {renderZTestOptions()}

                            <div className="mt-3">{renderConfidenceInterval()}</div>
                            {isITMDataShown ? <div className="mt-3">{renderDataType()}</div> : ""}
                            {moduleName === "secondary" && selectedStudy[0].study_type_method__title === "CLT" && analysisTypeValue.value === "box_analysis" && isJarAnalysis ? (<div className="mt-3">{renderJarOptions()}</div>) : ("")}
                            {analysisTypeValue.value === "box_analysis" && boxAnalysisTypeSelection.length == 2 && <div className="mt-3">{renderBoxAnalysisType()}</div>}
                            <div className="mt-3">{renderProducts()}</div>
                            {/* {showDialogSampleWarning && !isBeautyAndWellbeing && <SampleSizeWarning threshold={75} />} */}
                        </div>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            paddingBottom: "20px",
                        }}
                    >
                        {renderDualSelectionWarning()}
                        {!tTestWarningFlag && (
                            <p
                                style={{
                                    color: "#FF9800",
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                    margin: "0px 100px",
                                    textAlign: "center",
                                    paddingBottom: "20px",
                                }}
                            >
                                The same filter is not applied on all products, it is statistically incorrect to apply T-test.
                            </p>
                        )}
                        {showJarAnalysisNote && (
                            <p
                                style={{
                                    color: "#000000",
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                    margin: "10px 0 0 0",
                                    textAlign: "center",
                                    paddingBottom: "10px",
                                }}
                            >
                                Note - The above mentioned Parametric and Non-parametric T-Tests are not applicable to JAR variables
                            </p>
                        )}
                        {/* Add this new warning message for Oral Care box analysis */}
                        {isPercentageValues && checkedProductsForTest.length >= 2 && totalProducts > 2 && moduleName !== "preference" && moduleName !== "productPreference" && moduleName !== "fragranceAssociation" && !(selectedStudy[0].study_type_method__title === "CLT" && (moduleName === "overall" || moduleName === "secondary")) && (
                            <p
                                style={{
                                    color: "#000000",
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                    margin: "10px 0 0 0",
                                    textAlign: "left",
                                    paddingBottom: "10px",
                                    paddingTop: "5px",
                                }}
                            >
                                {variableCategory === "Just Right" || variableCategory === "JAR" ? (
                                    // Simplified note for Just Right and JAR
                                    "Note - The Significant T-Test cannot be applied to % for 3 or more products."
                                ) : (
                                    // Detailed note with instructions for other analyses
                                    <>Note - The Significant T-Test cannot be applied to % for 3 or more products. If you need to perform a t-test with 3 or more products, please switch the values from % to Mean.</>
                                )}
                            </p>
                        )}

                        {isFragranceAssociationSection && selectedSection === "fragranceAssociation" && checkedTestTypeForTest === 7 && checkedProductsForTest.length >= 2 && (
                            <p
                                style={{
                                    color: "#000000",
                                    fontSize: "0.75rem",
                                    fontWeight: "600",
                                    margin: "10px 0 0 0",
                                    textAlign: "left",
                                    paddingBottom: "10px",
                                    paddingTop: "5px",
                                }}
                            >
                                Note - The Significant T-Test cannot be applied on 3 or more products. If you need to perform a t-test with 3 or more
                                products, please switch the T-test option to &quot;Fisher&apos;s LSD pairwise comparisons (After data transformation)&quot;
                            </p>
                        )}
                        <div style={{ alignSelf: "start", marginBottom: "10px" }}>{showDialogSampleWarning && !isBeautyAndWellbeing && <SampleSizeWarning threshold={75} />}</div>
                        <Button
                            size="small"
                            className={disableResultButton ? "custom-btn  disabled" : "custom-btn custom-btn-outlined color-blue"}
                            variant="outlined"
                            disabled={disableResultButton}
                            onClick={moduleName === "fragranceAssociation" ? handleFragranceAssociationTTestDialogueSubmit : handleTTestDialogueSubmit}
                        >
                            Get Results
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

// Typechecking props for the missing filters tooltip
TTestDialog.propTypes = {
    functionsSettings: PropTypes.object,
    analysisLevelData: PropTypes.object,
    tabData: PropTypes.object,
    variableCategory: PropTypes.string,
    isITMDataShown: PropTypes.bool,
    tTestBtnDisabled: PropTypes.bool,
    isFragranceAssociationSection: PropTypes.bool,
    overallToggleEnabled: PropTypes.bool,
    percentageToggle: PropTypes.bool,
    isJarAnalysis: PropTypes.bool,
    module: PropTypes.string,
};

export default TTestDialog;
